import { FC, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import { CustomLineModel, QuickSalePlanModel } from '../models/QuickSaleModel';
import { getPlanVariants } from '../services/QuickSaleCRUD';
import { apiErrorMessage } from '../utils/quickSaleFormat';

type Line = { meal: number; meal_size: number; units: number };

type Props = {
  userId: number;
  plan: QuickSalePlanModel;
  initialLines?: Line[];
  onClose: () => void;
  onApply: (lines: Line[], allow800gr: boolean) => void;
};

const LARGE_FORMATS_VARIANT = 'large-formats';

function toLines(lines: CustomLineModel[] = []): Line[] {
  return lines.map(({ meal, meal_size, units }) => ({
    meal,
    meal_size,
    units,
  }));
}

/**
 * The custom recipes of a plan, edited over the page instead of on another
 * one. What is applied goes to the draft, so the price is simulated first.
 */
const PlanKindModal: FC<Props> = ({
  userId,
  plan,
  initialLines,
  onClose,
  onApply,
}) => {
  const intl = useIntl();
  const choices = plan.custom_meal_choices;
  const savedLines = plan.custom_option?.lines;
  const [lines, setLines] = useState<Line[]>(
    initialLines ||
      toLines(savedLines?.length ? savedLines : plan.current_lines)
  );
  const [loadingLarge, setLoadingLarge] = useState(false);

  const sizes = choices.flatMap((choice) => choice.sizes);
  const sizeOf = (sizeId: number) => sizes.find((size) => size.id === sizeId);
  const totalGrams = lines.reduce(
    (total, line) =>
      total + (sizeOf(line.meal_size)?.weight_in_gr || 0) * line.units,
    0
  );
  const periodDays = plan.period?.period;
  const allow800gr = lines.some(
    (line) => sizeOf(line.meal_size)?.needs_800gr_exception
  );

  function setLine(index: number, values: Partial<Line>) {
    setLines((current) =>
      current.map((line, i) => {
        if (i !== index) {
          return line;
        }
        const next = { ...line, ...values };
        if (values.meal !== undefined) {
          const meal = choices.find((choice) => choice.meal === values.meal);
          const keepsSize = meal?.sizes.some(
            (size) => size.id === next.meal_size
          );
          if (!keepsSize && meal?.sizes.length) {
            next.meal_size = meal.sizes[0].id;
          }
        }
        return next;
      })
    );
  }

  function addLine() {
    const first = choices[0];
    if (!first?.sizes.length) {
      return;
    }
    setLines((current) => [
      ...current,
      { meal: first.meal, meal_size: first.sizes[0].id, units: 1 },
    ]);
  }

  async function useLargeFormats() {
    setLoadingLarge(true);
    try {
      const { data } = await getPlanVariants(userId, plan.id);
      const variant = data.variants.find(
        (item) => item.key === LARGE_FORMATS_VARIANT
      );
      if (variant?.draft?.custom_lines) {
        setLines(variant.draft.custom_lines);
      } else {
        toast.info(intl.formatMessage({ id: 'quickSale.noLargeFormats' }));
      }
    } catch (error) {
      toast.error(
        apiErrorMessage(
          error,
          intl.formatMessage({ id: 'quickSale.genericError' })
        )
      );
    } finally {
      setLoadingLarge(false);
    }
  }

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <h4 className="mb-0">
          {intl.formatMessage({ id: 'quickSale.customPlanTitle' })}
        </h4>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-wrap gap-2 mb-5">
          <Button
            size="sm"
            variant="light"
            onClick={() => setLines(toLines(plan.current_lines))}
          >
            {intl.formatMessage({ id: 'quickSale.useCurrentRecipes' })}
          </Button>
          {!!savedLines?.length && (
            <Button
              size="sm"
              variant="light"
              onClick={() => setLines(toLines(savedLines))}
            >
              {intl.formatMessage({ id: 'quickSale.useSavedCustom' })}
            </Button>
          )}
          <Button
            size="sm"
            variant="light-primary"
            disabled={loadingLarge}
            onClick={useLargeFormats}
          >
            {loadingLarge && (
              <span className="spinner-border spinner-border-sm me-2" />
            )}
            {intl.formatMessage({ id: 'quickSale.useLargeFormats' })}
          </Button>
        </div>

        <table className="table table-row-dashed align-middle gy-2">
          <thead>
            <tr className="fw-bolder text-muted">
              <th>{intl.formatMessage({ id: 'quickSale.recipe' })}</th>
              <th>{intl.formatMessage({ id: 'quickSale.format' })}</th>
              <th className="w-100px">
                {intl.formatMessage({ id: 'quickSale.units' })}
              </th>
              <th className="w-50px" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const meal = choices.find((choice) => choice.meal === line.meal);
              return (
                <tr key={index}>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={line.meal}
                      aria-label={intl.formatMessage({
                        id: 'quickSale.recipe',
                      })}
                      onChange={(event) =>
                        setLine(index, { meal: Number(event.target.value) })
                      }
                    >
                      {!meal && <option value={line.meal}>{line.meal}</option>}
                      {choices.map((choice) => (
                        <option key={choice.meal} value={choice.meal}>
                          {choice.meal_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={line.meal_size}
                      aria-label={intl.formatMessage({
                        id: 'quickSale.format',
                      })}
                      onChange={(event) =>
                        setLine(index, {
                          meal_size: Number(event.target.value),
                        })
                      }
                    >
                      {(meal?.sizes || []).map((size) => (
                        <option key={size.id} value={size.id}>
                          {size.weight_in_gr} g
                          {size.needs_800gr_exception ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      min={1}
                      className="form-control form-control-sm"
                      value={line.units}
                      onChange={(event) =>
                        setLine(index, {
                          units: Math.max(1, Number(event.target.value) || 1),
                        })
                      }
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-icon btn-sm btn-light-danger"
                      title={intl.formatMessage({ id: 'quickSale.remove' })}
                      onClick={() =>
                        setLines((current) =>
                          current.filter((_, i) => i !== index)
                        )
                      }
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Button size="sm" variant="light" onClick={addLine}>
          <i className="fas fa-plus me-1" />
          {intl.formatMessage({ id: 'quickSale.addRecipe' })}
        </Button>

        <div className="d-flex justify-content-between mt-5 text-gray-700">
          <span>
            {intl.formatMessage(
              { id: 'quickSale.customTotal' },
              {
                grams: intl.formatNumber(totalGrams),
                perDay: periodDays ? Math.round(totalGrams / periodDays) : '-',
              }
            )}
          </span>
          {allow800gr && (
            <span className="text-primary">
              * {intl.formatMessage({ id: 'quickSale.enables800gr' })}
            </span>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          {intl.formatMessage({ id: 'quickSale.cancel' })}
        </Button>
        <Button
          variant="primary"
          disabled={!lines.length}
          onClick={() => onApply(lines, allow800gr)}
        >
          {intl.formatMessage({ id: 'quickSale.applyToDraft' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { PlanKindModal };
